import ReminderDialog from "../utils/reminderDialog";
import Translator from "../utils/translator";
import { DriveStore } from "../store/reducers";
import { observeStore } from "../utils/store";
import toastr from "../utils/toastr";
import { NetworkStates } from "../store/mobile/types";
import { toastOnlineId } from "../ui/topbar";
import { SubscriptionsService } from "./base";
import { UserService } from "../utils/users";
import { SecurityService } from "../utils/security";
import { Unsubscribe } from "../lib/redux";
import { MINUTES } from "../constants";

const SIGNAL_LOST_TITLE = "Signal lost";
// eslint-disable-next-line max-len
const DEVICE_OFFLINE_MESSAGE = "Your telematics device is offline. To keep your records accurate, enter status changes manually. Automatic tracking and log syncing will resume when the connection is restored.";
// eslint-disable-next-line max-len
const SERVER_UNAVAILABLE_MESSAGE = "The ELD server has been unavailable for 15 minutes. To keep your logs current, we recommend that you make manual status changes until the ELD server is available.";

export const OFFLINE_REMINDER_TIMEOUT = 15 * MINUTES;
export const IOX_REMINDER_TIMEOUT = 500;

export default class OfflineStatusReminder extends SubscriptionsService {
    private readonly offlineDialog: ReminderDialog;
    private readonly ioxDetachedDialog: ReminderDialog;
    private isOfflineMode = false;
    private readonly observers: Unsubscribe[];

    /**
     * Creates an instance of the OfflineStatusReminder class.
     *
     * @param translator - An instance of the Translator class for localizing dialog messages.
     * @param store - The application state store.
     * @param userService - Service for user-related operations.
     * @param securityService - Service for security-related operations.
     * @param featureFlagsService - Service for managing feature flags.
     */
    constructor(
        private readonly translator: Translator,
        private readonly store: DriveStore,
        private readonly userService: UserService,
        private readonly securityService: SecurityService
    ) {
        super();
        this.observers = [];
        observeStore(this.store, state => state.data.ELDJurisdiction, () => this.onELDJurisdictionChange());
        this.offlineDialog = new ReminderDialog(
            this.translator,
            this.store.getState().data.nightMode,
            OFFLINE_REMINDER_TIMEOUT,
            this.closeExistingPopup,
            SIGNAL_LOST_TITLE,
            () => {
                if (store.getState().mobile.online === NetworkStates.CLIENT_OFFLINE) {
                    return DEVICE_OFFLINE_MESSAGE;
                }
                return SERVER_UNAVAILABLE_MESSAGE;
            },
            this.canOpen.bind(this)
        );

        this.ioxDetachedDialog = new ReminderDialog(
            this.translator,
            this.store.getState().data.nightMode,
            IOX_REMINDER_TIMEOUT,
            () => {
                this.offlineDialog.close();
                this.closeExistingPopup();
            },
            "Telematics Cable Unplugged",
            () => {
                return "To keep your logs current, we recommend that you make manual status changes or reconnect the telematics cable.";
            },
            this.canOpen.bind(this)
        );
    }

    /**
     * Starts observing the store for relevant state changes and triggers appropriate actions.
     */
    startSubscriptions(): void {
        this.stopSubscriptions();
        this.observers.push(observeStore(this.store, state => [state.mobile.online, state.mobile.ioxAttached], () =>
            this.onlineStatusChanged(this.store.getState().mobile.online, this.store.getState().mobile.ioxAttached)));
        this.observers.push(observeStore(this.store, state => state.mobile.ioxAttached, () =>
            this.ioxAttached(this.store.getState().mobile.ioxAttached, this.store.getState().mobile.online)));
        this.observers.push(observeStore(this.store, state => state.mobile.background, this.backgroundStatusChanged.bind(this)));
    }

    /**
     * Stops observing the store for state changes and clears observers.
     */
    stopSubscriptions(): void {
        while (this.observers.length > 0) {
            let observer = this.observers.pop();
            observer && observer();
        }
    }

    /**
     * Closes any existing popups.
     */
    closeExistingPopup(this: void) {
        let toastEl = document.getElementById(toastOnlineId);
        toastEl && toastr.remove(toastEl);
    }

    /**
     * Handles the Iox Dialog when the telematics cable is attached or detached.
     *
     * @param attached - `true` if the cable is attached, `false` otherwise.
     * @param isOnline - The current network state.
     */
    ioxAttached(attached: boolean | undefined, isOnline: NetworkStates | undefined) {
        if (isOnline === NetworkStates.ONLINE) {
            return;
        }
        if (!attached) {
            this.ioxDetachedDialog && !this.ioxDetachedDialog.isOpening() && this.ioxDetachedDialog.open();
        } else {
            this.ioxDetachedDialog && this.ioxDetachedDialog.close();
        }
    }

    /**
     * Handles the Offline Dialog when the online status changes.
     *
     * @param isOnline - The current network state.
     * @param ioxAttached - `true` if the telematics cable is attached, `false` otherwise.
     */
    onlineStatusChanged(isOnline: NetworkStates | undefined, ioxAttached: boolean | undefined) {
        if (isOnline === NetworkStates.ONLINE) {
            this.offlineDialog.close();
            this.isOfflineMode = false;
        } else if (isOnline !== NetworkStates.OFFLINE) {
            this.closeExistingPopup();
            !ioxAttached && !this.isOfflineMode && !this.offlineDialog.isOpening() && this.offlineDialog.open(() => this.isOfflineMode = true);
        }
    }

    /**
     * Handles the state change when the app goes to the background or foreground.
     *
     * @param isBackground - `true` if the app is in the background, `false` otherwise.
     */
    backgroundStatusChanged(isBackground) {
        if (!isBackground) {
            this.offlineDialog.backgroundChanged();
        }
    }

    /**
     * Sets the timeout period for the offline reminder dialog.
     *
     * @param timeout - The new timeout period in milliseconds.
     */
    setTimeout(timeout) {
        this.offlineDialog.setTimeout(timeout);
    }

    /**
     * Handles changes in the ELD jurisdiction.
     */
    onELDJurisdictionChange() {
        if (this.store.getState().data.ELDJurisdiction === "EWD") {
            this.stopSubscriptions();
        } else {
            this.startSubscriptions();
        }
    }

    /**
     * Destroys the instance, closing any open dialogs.
     */
    destroy(): void {
        this.offlineDialog?.close();
        this.ioxDetachedDialog?.close();
    }

    /**
     * Checks if the reminder dialog can be opened based on feature flags and user permissions.
     *
     * @returns `true` if the dialog can be opened, `false` otherwise.
     */
    private canOpen(): boolean {
        let currentUser = this.userService.getCurrentUser();
        return this.securityService.can("useHos", currentUser?.id) && this.userService.isDriverUsingUI(currentUser?.name || "");
    }
}